import { useQuery } from '@tanstack/vue-query'
import { Ref, computed } from 'vue'

import { useUrl } from '@/composables/url-helpers'
import { DEFAULT_CATEGORY_PAGE_SIZE } from '@/constants/general'
import { useI18nAddon } from '@/i18n/i18n'
import { usePersisterFn } from '@/query/persister-plugin'
import { Persisters } from '@/query/shared'
import { useAppStore } from '@/store/app-store'
import { APIRedirect, CategoryType, RedirectMechanism, RedirectReason, ShortGameType, ShortGameZipType } from '@/types'
import { unzipShortGame, zipAPIGame } from '@/utils/helpers'
import { useAPI } from '@/utils/requests/api-plugin'
import { isAPIRedirect, isValidAPICategorySeoInfo } from '@/utils/requests/utils'

type Category = {
    id: string
    categoryType: CategoryType
    total: number
    totalPages: number
}

type UseCategoryPageDataParams = {
    categoryName: Ref<string>
    pageType: Ref<CategoryType>
    page: Ref<number>
    pinnedGame: Ref<string | undefined>
}

export const useCategoryPageData = ({ categoryName, pageType, page, pinnedGame }: UseCategoryPageDataParams) => {
    const appStore = useAppStore()
    const { getCategoryRoute } = useUrl()
    const { currentLocale } = useI18nAddon()
    const { getGamesByCategories, getGamesCategoriesSeoById } = useAPI()

    // Query 1: Category SEO Info
    const seoQueryKey = computed(() => ['category-seo-info', categoryName.value, pageType.value, currentLocale.value])
    const seoQueryPersister = usePersisterFn(Persisters.MediumCardinality)

    const { data: seoData, suspense: seoSuspense } = useQuery({
        queryKey: seoQueryKey,
        queryFn: async () => {
            const categoryInfoData = await getGamesCategoriesSeoById({
                id: categoryName.value,
                locale: currentLocale.value,
                type: pageType.value,
            })

            if (isAPIRedirect(categoryInfoData)) {
                return { categoryRedirect: categoryInfoData }
            }

            if (isValidAPICategorySeoInfo(categoryInfoData) && categoryInfoData.categoryType !== pageType.value) {
                return {
                    categoryRedirect: {
                        redirectTarget: {
                            type: 'category',
                            cat_type: categoryInfoData.categoryType,
                            cat_hru: categoryName.value,
                            reason: RedirectReason.CATEGORY_TYPE_MISMATCH,
                            mechanism: RedirectMechanism.FRONTEND_REDIRECT,
                        },
                        status: 301,
                    } as const,
                }
            }

            const getSubCategories = (type: 'pathCategories' | 'subCategories') =>
                categoryInfoData[type]?.map((subCategory) => ({
                    ...subCategory,
                    href: getCategoryRoute({ hru: subCategory.hru, type: subCategory.categoryType }),
                })) || []

            return {
                categoryInfo: {
                    ...categoryInfoData,
                    pathCategories: getSubCategories('pathCategories'),
                    subCategories: getSubCategories('subCategories'),
                    fullTitle: categoryInfoData.description,
                },
            }
        },
        persister: seoQueryPersister,
    })

    // Query 2: Category Games Data
    const gamesQueryKey = computed(() => [
        'category-games-data',
        categoryName.value,
        pageType.value,
        page.value,
        pinnedGame.value,
        currentLocale.value,
        appStore.platform,
        appStore.rankingParams?.clusterId,
        appStore.rankingParams?.audience,
        appStore.isDebugMode,
        appStore.contentScope,
    ])
    const firstPagePersister = usePersisterFn(Persisters.MediumCardinality)
    const gamesQueryPersister = computed(() => {
        if (page.value === 1) {
            return firstPagePersister
        }
        return undefined
    })

    const { data: gamesData, suspense: gamesSuspense } = useQuery({
        queryKey: gamesQueryKey,
        queryFn: async () => {
            const result: {
                games: ShortGameZipType[]
                category?: Category
                categoryRedirect?: APIRedirect
            } = {
                games: [],
            }

            // contentScope indicates we want to show specific content to user
            // meanwhile games on unsupported platforms need to be shown only for SEO
            // so defined contentScope disables this flag
            const includeUnsupportedPlatforms = !appStore.contentScope

            try {
                if (isNaN(page.value)) {
                    // eslint-disable-next-line no-throw-literal
                    throw 404
                }

                const {
                    categories: [fetchedCategory],
                } = await getGamesByCategories({
                    categoriesIds: [categoryName.value],
                    pagination: {
                        page: page.value,
                        pageSize: DEFAULT_CATEGORY_PAGE_SIZE,
                    },
                    platform: appStore.platform,
                    locale: currentLocale.value,
                    withDebugInfo: appStore.isDebugMode,
                    pageType: pageType.value,
                    pinnedGame: pinnedGame.value,
                    rankingClusterId: appStore.rankingParams?.clusterId,
                    rankingAudience: appStore.rankingParams?.audience,
                    includeUnsupportedPlatforms,
                    scope: appStore.contentScope,
                })

                if (!fetchedCategory) {
                    // eslint-disable-next-line no-throw-literal
                    throw 404
                }

                if (page.value > 1 && fetchedCategory.games.length === 0) {
                    // eslint-disable-next-line no-throw-literal
                    throw 404
                }

                result.games = fetchedCategory?.games.map(zipAPIGame) || []
                result.category = {
                    id: fetchedCategory.id,
                    categoryType: fetchedCategory.categoryType,
                    total: fetchedCategory.totalCount,
                    totalPages: Math.ceil(fetchedCategory.totalCount / DEFAULT_CATEGORY_PAGE_SIZE),
                }
            } catch (error) {
                if (error !== 404) {
                    throw error
                }
                // If games fetch fails with 404, this will be handled by the component
            }

            return result
        },
        persister: gamesQueryPersister,
    })

    // Combine the results
    const games = computed<ShortGameType[]>(() => gamesData.value?.games.map(unzipShortGame) || [])

    const pageSize = computed(() => DEFAULT_CATEGORY_PAGE_SIZE)
    const category = computed(() => gamesData.value?.category)
    const categoryInfo = computed(() => seoData.value?.categoryInfo)

    // Prioritize SEO redirect over games redirect
    const categoryRedirect = computed(() => seoData.value?.categoryRedirect || gamesData.value?.categoryRedirect)

    // Combined suspense for SSR - both queries must complete
    const preloadCategoryPageData = async () => {
        await Promise.all([seoSuspense(), gamesSuspense()])
    }

    return {
        games,
        pageSize,
        category,
        categoryInfo,
        categoryRedirect,
        preloadCategoryPageData,
    }
}
